// API クライアント。dev なりすましヘッダを付与し、エラーは繁体字メッセージで投げる。

import { getDevUser } from "./session";
import type {
  Attachment,
  AuditEntry,
  Comment,
  CommentChannel,
  Contract,
  ContractFormValues,
  EvaluationValues,
  User,
} from "./types";

// 認証ヘッダ（dev なりすまし）。FormData/blob 経路で request() を使えないとき用。
function authHeaders(): Headers {
  const headers = new Headers();
  const devUser = getDevUser();
  if (devUser) headers.set("x-dev-user", devUser);
  return headers;
}

class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  const devUser = getDevUser();
  if (devUser) headers.set("x-dev-user", devUser);
  if (init.body) headers.set("content-type", "application/json");

  const res = await fetch(`/api${path}`, { ...init, headers });
  const text = await res.text();
  const data = text ? JSON.parse(text) : {};

  if (!res.ok) {
    throw new ApiError(res.status, data?.error ?? "發生未預期的錯誤。");
  }
  return data as T;
}

// フォーム値 → API ペイロード（数値化・空文字除去）。
function toPayload(values: ContractFormValues) {
  return {
    contract_name: values.contract_name.trim(),
    counterparty: values.counterparty.trim(),
    contract_type: values.contract_type,
    continuity: values.continuity,
    amount: values.amount.trim() === "" ? null : Number(values.amount),
    currency: values.currency || null,
    amount_type: values.amount_type || null,
    period_start: values.period_start || null,
    period_end: values.period_end || null,
    special_clauses: values.special_clauses,
    next_renewal_date: values.next_renewal_date || null,
  };
}

export const api = {
  me: () => request<{ user: User; devLoginAvailable: boolean }>("/me"),

  listContracts: () => request<{ contracts: Contract[] }>("/contracts"),

  getContract: (id: number) => request<{ contract: Contract }>(`/contracts/${id}`),

  createContract: (values: ContractFormValues, action: "draft" | "submit") =>
    request<{ contract: Contract }>("/contracts", {
      method: "POST",
      body: JSON.stringify({ ...toPayload(values), action }),
    }),

  updateContract: (id: number, values: ContractFormValues) =>
    request<{ contract: Contract }>(`/contracts/${id}`, {
      method: "PUT",
      body: JSON.stringify(toPayload(values)),
    }),

  submitContract: (id: number) =>
    request<{ contract: Contract }>(`/contracts/${id}/submit`, { method: "POST" }),

  approveContract: (id: number) =>
    request<{ contract: Contract }>(`/contracts/${id}/approve`, { method: "POST" }),

  rejectContract: (id: number, reason: string) =>
    request<{ contract: Contract }>(`/contracts/${id}/reject`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  updateEvaluation: (id: number, values: EvaluationValues) =>
    request<{ contract: Contract }>(`/contracts/${id}/evaluation`, {
      method: "PUT",
      body: JSON.stringify({
        pl_impact: values.pl_impact || null,
        pl_impact_note: values.pl_impact_note || null,
        risk_level: values.risk_level || null,
        classification: values.classification || null,
        hq_review_status: values.hq_review_status || null,
      }),
    }),

  getComments: (id: number) =>
    request<{ staffContact: Comment[]; meetingMemo: Comment[] | null }>(
      `/contracts/${id}/comments`
    ),

  postComment: (id: number, channel: CommentChannel, body: string) =>
    request<{ comment: Comment }>(`/contracts/${id}/comments`, {
      method: "POST",
      body: JSON.stringify({ channel, body }),
    }),

  listAudit: (params: { action?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.action) q.set("action", params.action);
    if (params.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return request<{ entries: AuditEntry[] }>(`/audit${qs ? `?${qs}` : ""}`);
  },

  listAttachments: (id: number) =>
    request<{ attachments: Attachment[] }>(`/contracts/${id}/attachments`),

  uploadAttachment: async (id: number, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(`/api/contracts/${id}/attachments`, {
      method: "POST",
      headers: authHeaders(), // content-type はブラウザが multipart 境界付きで設定
      body: fd,
    });
    const text = await res.text();
    const data = text ? JSON.parse(text) : {};
    if (!res.ok) throw new ApiError(res.status, data?.error ?? "上傳失敗。");
    return data as { attachment: Attachment };
  },

  // 認証＋可視範囲を通してダウンロード（直リンク不可）。blob 取得後にダウンロード起動。
  downloadAttachment: async (id: number, filename: string) => {
    const res = await fetch(`/api/attachments/${id}`, { headers: authHeaders() });
    if (!res.ok) {
      const text = await res.text();
      let msg = "下載失敗。";
      try {
        msg = JSON.parse(text)?.error ?? msg;
      } catch {
        /* noop */
      }
      throw new ApiError(res.status, msg);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },

  deleteAttachment: (id: number) =>
    request<{ ok: boolean }>(`/attachments/${id}`, { method: "DELETE" }),
};

export { ApiError };
